import styled from 'styled-components';

import { PLAYERS, MOBILE, ANIM, GRADIENTS, SPACE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Text } from '../../../../components';

// The roster is a column of cards at every width (the mobile design, promoted to
// desktop). Cards carry their own surface + spacing, so no inter-card separator is
// needed — the gap does the work. Sits inside the page-level list panel
// (`players.styles` → `Container`), which also holds the pagination footer.
export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// The phone card's grid areas, with and without a "came from" row.
const CAME_ROW_AREAS = "'avatar email' 'came came' 'stats stats'";
const PLAIN_AREAS = "'avatar email' 'stats stats'";

// One player card: avatar + email (and, on a wide screen, where the player came from) on top, the labelled figures
// below. On a phone "came from" takes its own row under the identity, only when it has something to link to
// (`$cameRow`): a plain "Direct" is left out there, so most cards stay their usual height.
export const Card = styled.div<{ $cameRow?: boolean }>`
  ${hoverWash}
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  grid-template-areas:
    'avatar email came'
    'stats  stats stats';
  align-items: center;
  column-gap: 14px;
  row-gap: 16px;
  padding: 18px 20px;
  border-radius: 16px;
  background: ${PLAYERS.rowBg};
  cursor: pointer;
  outline: none;
  /* Own stacking context: keeps the copy button's z-index scoped to the card, so
     it never paints above the page header / scrim when the card scrolls up. */
  isolation: isolate;

  ${MOBILE} {
    grid-template-columns: 40px minmax(0, 1fr);
    grid-template-areas: ${(p) => (p.$cameRow ? CAME_ROW_AREAS : PLAIN_AREAS)};
    column-gap: 12px;
    row-gap: 14px;
    padding: 16px;
    border-radius: 14px;
  }
`;

export const AvatarCell = styled.div`
  grid-area: avatar;
  display: flex;
  align-items: center;
`;

// The identity block: the email row, with the registration meta line under it.
export const EmailCell = styled.div`
  grid-area: email;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
`;

export const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

// Registration date · acquisition source · flags — one truncating line, so the new
// registration-based sorts ("Newest first", "Recently seen") show their sort key.
export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

export const MetaText = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: ${PLAYERS.pageMeta};
`;


export const EmailText = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 600;
  color: ${PLAYERS.email};
`;

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  color: ${PLAYERS.copyGlyph};
  background: ${PLAYERS.copyBg};
  cursor: pointer;
  opacity: 0.85;
  /* Sits above the card's hover-wash ::after so it stays clickable + recolours. */
  position: relative;
  z-index: 1;
  transition: background ${ANIM.INTERACT_MS}ms ease, opacity ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${PLAYERS.copyBgHover};
    opacity: 1;
    outline: none;
  }
`;

// The figures, beneath the avatar + email, divided off by a hairline. On mobile they wrap two per row so each money
// value keeps enough width to read.
/** The card's figures: seven across on a wide screen, two per row on a phone. */
export const STAT_COLUMNS = 7;

export const Stats = styled.div`
  grid-area: stats;
  display: grid;
  grid-template-columns: repeat(${STAT_COLUMNS}, minmax(0, 1fr));
  gap: ${SPACE[12]};
  padding-top: ${SPACE[16]};
  border-top: 1px solid ${PLAYERS.hairline};
  border-image: ${GRADIENTS.dividerStyledH} 1;

  ${MOBILE} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${SPACE[12]} ${SPACE[8]};
    padding-top: ${SPACE[12]};
  }
`;

export const MetaCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const CellLabel = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${PLAYERS.headerLabel};
`;

export const MetaValue = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${PLAYERS.meta};
  white-space: nowrap;
`;

export const MoneyValue = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${PLAYERS.money};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

/** A figure that doesn't apply or isn't known ("–"). */
export const NoValue = styled(MoneyValue)`
  color: ${PLAYERS.headerLabel};
`;

export const WalletValue = styled(MoneyValue)`
  color: ${PLAYERS.walletAccent};
`;

// --- Came from -----------------------------------------------------------------
// Where the player came from: a compact linked chip (the asset's kind small, then its name), the landing page it went
// through under it. A column at the card's right edge on a wide screen; its own row on a phone.
export const CameCell = styled.div<{ $hideOnPhone?: boolean }>`
  grid-area: came;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${SPACE[4]};
  min-width: 0;
  /* A width cap, not spacing: a long asset name truncates instead of squeezing the email. */
  max-width: 280px;

  ${MOBILE} {
    display: ${(p) => (p.$hideOnPhone ? 'none' : 'flex')};
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    column-gap: ${SPACE[8]};
    max-width: none;
  }
`;

// A deep link sits above the card's hover-wash ::after (like the copy button) so it stays clickable.
const aboveWash = `
  position: relative;
  z-index: 1;
`;

export const FromChip = styled.a`
  ${aboveWash}
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  max-width: 100%;
  padding: ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  background: ${PLAYERS.copyBg};
  color: ${PLAYERS.email};
  text-decoration: none;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${PLAYERS.copyBgHover};
    outline: none;
  }
`;

export const FromKind = styled(Text)`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${PLAYERS.headerLabel};
`;

export const FromName = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
`;

// "via <landing page>", under the chip.
export const FromVia = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${SPACE[4]};
  min-width: 0;
  max-width: 100%;
  font-size: 12px;
  color: ${PLAYERS.pageMeta};
`;

export const FromViaWord = styled(Text)`
  flex: 0 0 auto;
`;

export const FromViaLink = styled.a`
  ${aboveWash}
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: ${PLAYERS.meta};
  text-decoration: underline;
  text-decoration-color: ${PLAYERS.hairline};
  text-underline-offset: 3px;

  &:hover,
  &:focus-visible {
    color: ${PLAYERS.email};
    text-decoration-color: currentColor;
    outline: none;
  }
`;

// "Direct", or a campaign the brand's links do not know: muted, not a link.
export const FromMuted = styled(Text)`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: ${PLAYERS.pageMeta};
`;

// --- Loading skeleton cards --------------------------------------------------
export const SkCard = styled.div`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  grid-template-areas:
    'avatar email came'
    'stats  stats stats';
  align-items: center;
  column-gap: 14px;
  row-gap: 16px;
  padding: 18px 20px;
  border-radius: 16px;
  background: ${PLAYERS.rowBg};

  ${MOBILE} {
    grid-template-columns: 40px minmax(0, 1fr);
    grid-template-areas:
      'avatar email'
      'stats  stats';
    column-gap: 12px;
    row-gap: 14px;
    padding: 16px;
    border-radius: 14px;
  }
`;

// The skeleton's "came from" column (a wide screen only, like the cards' usual Direct).
export const SkCame = styled.div`
  grid-area: came;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${SPACE[4]};

  ${MOBILE} {
    display: none;
  }
`;

// Two stacked lines in the skeleton's identity block, matching the real card's
// email + meta rows (a single bar would make the list jump when data lands).
export const SkIdentity = styled.div`
  grid-area: email;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  min-width: 0;
`;

export const SkStats = styled(Stats)``;
